// src/components/acting-org/ActingOrgBar.tsx
//
// Selector «Empresa» + banner «Trabajando en: <Empresa> · como <consultora>»
// (ADR-007 · Contrato F1). Reutilizable por cualquier módulo que adopte el acceso
// delegado: el módulo manda `actingOrgHeaders()` en SUS llamadas y monta esta
// barra arriba de sus pantallas. Hoy lo usa Diagnósticos.
//
// - Opciones: mi organización + mis engagements USABLES (rol consultora, activo,
//   vigente y con el scope del módulo).
// - Nunca elige sola: sin elección explícita = mi org (sin header).
// - Al montar re-valida la empresa recordada; si ya no sirve → aviso y mi org.
// Solo tokens semánticos del tema; accesible (menú con teclado, Esc, foco).

import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { AlertTriangle, ArrowLeftRight, Building2, Check, ChevronDown, Loader2, LogOut, Settings2, X } from 'lucide-react';

import {
  clearActingOrg,
  dismissActingNotice,
  revalidateActingOrg,
  setActingOrg,
  useActingOrg,
} from '@/lib/actingOrg';
import {
  ENGAGEMENTS_CHANGED,
  MODO_LABEL,
  engagementBlocker,
  engagementUsable,
  listEngagements,
  type Engagement,
} from '@/lib/engagements';

export interface ActingOrgBarProps {
  /** Scope mínimo del módulo (p. ej. `diagnostics:read`). */
  scope: string;
  /** Nombre de MI organización (la consultora). */
  myOrgName: string;
  /** Ir a la pantalla donde se administran las empresas (opcional). */
  onManage?: () => void;
  /** Cambia cuando la lista de engagements cambió (p. ej. tras agregar una empresa). */
  refreshKey?: unknown;
}

export function ActingOrgBar({ scope, myOrgName, onManage, refreshKey }: ActingOrgBarProps) {
  const { acting, notice } = useActingOrg();
  const [engagements, setEngagements] = useState<Engagement[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [selError, setSelError] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const menuId = useId();

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const list = await listEngagements();
      setEngagements(list);
      await revalidateActingOrg(scope, list);
    } catch (e) {
      setEngagements(null);
      // 404 = el backend aún no expone engagements: el módulo sigue en mi org.
      const status = (e as { status?: number }).status;
      setLoadError(status === 404 ? '' : 'No pudimos cargar tus empresas.');
      await revalidateActingOrg(scope, undefined);
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);
  useEffect(() => {
    const on = () => void load();
    window.addEventListener(ENGAGEMENTS_CHANGED, on);
    return () => window.removeEventListener(ENGAGEMENTS_CHANGED, on);
  }, [load]);

  // Menú: clic fuera / Esc cierra y devuelve el foco al botón.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        btnRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    wrapRef.current?.querySelector<HTMLElement>('[role="menuitemradio"]:not([aria-disabled="true"])')?.focus();
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const onMenuKey = (e: ReactKeyboardEvent) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const items = Array.from(wrapRef.current?.querySelectorAll<HTMLElement>('[role="menuitemradio"],[role="menuitem"]') ?? []);
    const i = items.indexOf(document.activeElement as HTMLElement);
    const next = items[(i + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length];
    next?.focus();
  };

  const choose = (e: Engagement | null) => {
    setSelError('');
    setOpen(false);
    if (!e) {
      clearActingOrg();
    } else {
      try {
        setActingOrg(e, scope);
      } catch (err) {
        setSelError((err as Error).message);
      }
    }
    btnRef.current?.focus();
  };

  const current = acting ? acting.nombre : myOrgName;
  const consultora = acting?.consultora_nombre || myOrgName;
  // Solo empresas donde MI org es la consultora, el acceso está activo y vigente y
  // trae el permiso del módulo (contrato F1). Las pausadas/revocadas viven en «Mis empresas».
  const list = (engagements ?? []).filter((e) => engagementUsable(e, scope));

  return (
    <div className="mb-4 flex flex-col gap-2">
      <div className="flex flex-col items-start gap-1.5 sm:flex-row sm:items-center sm:gap-2">
        <span id={`${menuId}-label`} className="text-xs font-medium text-muted-foreground">
          Empresa
        </span>
        <div ref={wrapRef} className="relative min-w-0">
          <button
            ref={btnRef}
            type="button"
            aria-haspopup="menu"
            aria-expanded={open}
            aria-controls={open ? menuId : undefined}
            aria-labelledby={`${menuId}-label ${menuId}-value`}
            onClick={() => setOpen((v) => !v)}
            className="flex min-h-[40px] min-w-0 max-w-[calc(100vw-64px)] items-center gap-2 rounded-xl border border-border bg-card px-3 py-1.5 text-sm text-foreground hover:bg-muted sm:max-w-sm"
          >
            <span aria-hidden className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${acting ? 'bg-primary text-primary-foreground' : 'bg-primary/10 text-primary'}`}>
              <Building2 size={14} />
            </span>
            <span id={`${menuId}-value`} className="truncate font-medium">{current}</span>
            {loading ? (
              <Loader2 size={14} aria-label="Cargando empresas" className="shrink-0 animate-spin text-muted-foreground" />
            ) : (
              <ChevronDown size={14} aria-hidden className="shrink-0 text-muted-foreground" />
            )}
          </button>
          {open && (
            <div
              id={menuId}
              role="menu"
              aria-label="Elegir empresa"
              onKeyDown={onMenuKey}
              className="absolute left-0 top-full z-50 mt-2 w-[min(340px,calc(100vw-64px))] rounded-xl border border-border bg-popover p-1.5 text-popover-foreground shadow-xl"
            >
              <p className="px-2.5 pb-1 pt-1.5 text-xs font-medium text-muted-foreground">Mi organización</p>
              <button
                type="button"
                role="menuitemradio"
                aria-checked={!acting}
                onClick={() => choose(null)}
                className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm hover:bg-muted focus:bg-muted focus:outline-none"
              >
                <span className="min-w-0 flex-1 truncate font-medium">{myOrgName}</span>
                {!acting && <Check size={15} aria-hidden className="text-primary" />}
              </button>
              <p className="mt-1 border-t border-border px-2.5 pb-1 pt-2 text-xs font-medium text-muted-foreground">Empresas que atiendes</p>
              {list.length === 0 ? (
                <p className="px-2.5 py-2 text-sm text-muted-foreground">
                  {loading ? 'Cargando…' : loadError || 'No tienes empresas activas. Agrégalas en «Mis empresas».'}
                </p>
              ) : (
                <ul className="max-h-72 overflow-y-auto">
                  {list.map((e) => {
                    const ok = engagementUsable(e, scope);
                    const on = acting?.engagement_id === e.id;
                    return (
                      <li key={e.id}>
                        <button
                          type="button"
                          role="menuitemradio"
                          aria-checked={on}
                          aria-disabled={!ok}
                          onClick={() => (ok ? choose(e) : setSelError(`No puedes entrar a ${e.cliente_nombre}: ${engagementBlocker(e, scope)}.`))}
                          className={`flex w-full items-start gap-2 rounded-lg px-2.5 py-2 text-left text-sm focus:outline-none ${ok ? 'hover:bg-muted focus:bg-muted' : 'cursor-not-allowed opacity-60 focus:bg-muted'}`}
                        >
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-medium">{e.cliente_nombre}</span>
                            <span className="block text-xs text-muted-foreground">
                              {MODO_LABEL[e.modo]}
                              {!ok && ` · ${engagementBlocker(e, scope)}`}
                            </span>
                          </span>
                          {on && <Check size={15} aria-hidden className="mt-0.5 text-primary" />}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              {onManage && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    onManage();
                  }}
                  className="mt-1 flex w-full items-center gap-2 rounded-lg border-t border-border px-2.5 py-2 text-left text-sm text-primary hover:bg-muted focus:bg-muted focus:outline-none"
                >
                  <Settings2 size={15} aria-hidden /> Administrar mis empresas
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {acting && (
        <div
          role="region"
          aria-label="Empresa activa"
          className="flex flex-col gap-2 rounded-2xl border border-primary/30 bg-primary/10 px-3 py-2.5 text-sm text-foreground sm:flex-row sm:items-center sm:px-4"
        >
          <span className="flex min-w-0 flex-1 items-start gap-2.5">
            <ArrowLeftRight size={16} aria-hidden className="mt-0.5 shrink-0 text-primary" />
            <span className="min-w-0">
              Trabajando en: <strong className="font-semibold">{acting.nombre}</strong>
              <span className="text-muted-foreground"> · como </span>
              <span className="font-medium">{consultora}</span>
              <span className="ml-2 inline-flex rounded-full bg-card px-2 py-0.5 align-middle text-[11px] font-medium text-muted-foreground">
                {MODO_LABEL[acting.modo]}
              </span>
            </span>
          </span>
          <button
            type="button"
            onClick={() => choose(null)}
            className="inline-flex min-h-[36px] shrink-0 items-center justify-center gap-1.5 self-start rounded-xl border border-border bg-card px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted sm:self-auto"
          >
            <LogOut size={14} aria-hidden /> Volver a mi org
          </button>
        </div>
      )}

      {(notice || selError) && (
        <div role="alert" className="flex items-start gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-foreground">
          <AlertTriangle size={16} aria-hidden className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
          <span className="min-w-0 flex-1">{selError || notice}</span>
          <button
            type="button"
            aria-label="Cerrar aviso"
            onClick={() => {
              setSelError('');
              dismissActingNotice();
            }}
            className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
